import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import clsx from 'clsx';
import i18n from 'i18n-js';
import moment from 'moment';
import { removeEmpty } from 'utils';
import { Card } from 'components/UI';
import Box from '@material-ui/core/Box';
import Loading from 'components/Loading';
import Chip from '@material-ui/core/Chip';
import VersionStatus from './VersionStatus';
import Constants from 'constants/documents';
import Button from '@material-ui/core/Button';
import Popover from '@material-ui/core/Popover';
import MenuItem from '@material-ui/core/MenuItem';
import VersionFileInput from './VersionFileInput';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import StarOutlinedIcon from '@material-ui/icons/StarOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { DocumentsActions } from 'state/modules/documents';
import { transformAsyncErrors } from 'utils/utils';
import VersionModal from 'components/Documents/VersionModal';

const useStyles = makeStyles((theme) => ({
  title: {
    fontSize: 14,
    fontWeight: 500,
  },
  expand: {
    transform: 'rotate(0deg)',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
  loadingContainer: {
    height: 150,
    width: '100%',
  },
  emptyPlaceholder: {
    color: theme.palette.text.subtitle,
  },
  statusItems: {
    color: '#4D4F5C',
    fontSize: theme.typography.pxToRem(14),
    padding: `${theme.spacing(1)}px ${theme.spacing(2.5)}px`,
  },
  statusButton: {
    color: theme.palette.text.primary,
    marginRight: 7,
    fontWeight: 400,
    textTransform: 'none',
  },
  PopoverPaper: {
    boxShadow: '0 1px 3px rgba(0,0,0,.2);',
  },
  defaultVersionText: {
    color: '#FFB027',
  },
  defaultVersionIcon: {
    color: '#FFB027',
    marginRight: 8,
  },
  viewDetailsAction: {
    textTransform: 'none',
    fontWeight: 500,
  },
  detailsListItem: {
    borderBottom: '1px solid #EEEEEE',
  },
  detailsListText: {
    fontSize: 13,
  },
  chipContainer: {
    flexWrap: 'wrap',
    '& > *': {
      margin: theme.spacing(0.5),
    },
  },
  tagsText: {
    marginTop: theme.spacing(0.85),
  },
  detailsListSecondText: {
    fontSize: 13,
    textAlign: 'right',
  },
}));

function WidgetHeader({ status, onChangeStatus, isDefault }) {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  return isDefault ? (
    <Box
      flex="1"
      display="flex"
      flexDirection="row"
      alignItems="center"
      justifyContent="space-between"
    >
      <Typography className={classes.title} variant="subtitle2">
        {i18n.t('documents.version_details')}
      </Typography>
      {status ? (
        <Button
          size="small"
          color="primary"
          onClick={handleClick}
          endIcon={<ExpandMoreIcon className={clsx(classes.expand, open && classes.expandOpen)} />}
          className={classes.statusButton}
        >
          <VersionStatus status={status} defaultMessage={i18n.t('common.status')} />
        </Button>
      ) : null}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          className: classes.PopoverPaper,
        }}
      >
        <Box>
          {Constants.statuses.map((versionStatus) => (
            <MenuItem
              key={versionStatus}
              className={classes.statusItems}
              onClick={() => {
                handleClose();
                onChangeStatus(versionStatus);
              }}
            >
              <VersionStatus status={versionStatus} />
            </MenuItem>
          ))}
        </Box>
      </Popover>
    </Box>
  ) : (
    i18n.t('documents.version_details')
  );
}

function VersionDetailsWidget({
  tags,
  id,
  document,
  handleChange,
  handleRemoveFile,
  handleUploadFile,
  onViewDetails,
  onTagClick,
  showDetailsBtn,
  isUploading,
}) {
  const [asyncErrors, setAsyncErrors] = useState({});
  const [versionModal, setVersionModal] = useState(false);
  const { localeFormat } = useSelector((state) => state.organizations);
  const dispatch = useDispatch();
  const version = useSelector((state) => state.documents?.versions?.current || {});
  const fetchingVersion = useSelector((state) => state.documents?.fetchingDocument || false);
  const fetchedUploadFile = useSelector((state) => state.documents?.fetchedUploadFile || false);

  const classes = useStyles();
  const file = useMemo(() => {
    if (version && version.files) {
      return version?.files[0];
    }
    return undefined;
  }, [version]);

  useEffect(() => {
    if (id) {
      dispatch(DocumentsActions.fetchVersionById(document, id));
    }
  }, [document, id, dispatch]); // eslint-disable-line

  const onChangeAttr = (key, value) => {
    if (key === 'file') {
      return handleUploadFile(version.id, value);
    }
    return handleChange({ ...removeEmpty(version), [key]: value }, version.id);
  };

  const toggleVersionModal = () => {
    if (versionModal) {
      setAsyncErrors({});
    }
    setVersionModal(!versionModal);
  };

  const onEdit = async (data) => {
    const response = await dispatch(
      DocumentsActions.updateVersion(document, version?.id, data, null, true)
    );
    if (response.ok) {
      toggleVersionModal();
    } else {
      setAsyncErrors({
        ...asyncErrors,
        updateVersion: transformAsyncErrors(response?.data?.details),
      });
    }
  };

  const items = [
    {
      title: i18n.t('documents.edit_version'),
      can: { action: 'update', name: 'document_version' },
      onClick: toggleVersionModal,
    },
  ];

  return (
    <Card
      title={
        <WidgetHeader
          status={version?.status}
          isDefault={version.default}
          onChangeStatus={(status) => onChangeAttr('status', status)}
        />
      }
      menuItems={version?.default ? items : []}
      headerActionType="vertical"
      data-cy="versions-details-card"
    >
      {fetchingVersion && (
        <Box
          className={classes.loadingContainer}
          justifyContent="center"
          alignItems="center"
          display="flex"
        >
          <Loading />
        </Box>
      )}
      {Object.keys(version)?.length > 0 && !fetchingVersion && (
        <>
          <Box>
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
            >
              {version.default || showDetailsBtn ? (
                <Box display="flex" flexDirection="row" alignItems="center">
                  <StarOutlinedIcon className={classes.defaultVersionIcon} />
                  <Typography variant="subtitle2" className={classes.defaultVersionText}>
                    {i18n.t('documents.default_version')}
                  </Typography>
                </Box>
              ) : null}
              {showDetailsBtn ? (
                <Button
                  className={classes.viewDetailsAction}
                  size="small"
                  color="primary"
                  onClick={() => onViewDetails(version.id)}
                >
                  {i18n.t('common.view_details')}
                </Button>
              ) : null}
            </Box>
            {version?.created_time && (
              <Box
                pb={1}
                pt={1}
                display="flex"
                flexDirection="row"
                alignItems="center"
                justifyContent="space-between"
                className={classes.detailsListItem}
              >
                <Typography variant="subtitle2" className={classes.detailsListText}>
                  {i18n.t('documents.creation_date')}:
                </Typography>
                <Typography variant="body2" className={classes.detailsListSecondText}>
                  {moment(version.created_time).format(localeFormat)}
                </Typography>
              </Box>
            )}
            <Box className={classes.detailsList}>
              <Box
                pb={1}
                pt={1}
                display="flex"
                flexDirection="row"
                alignItems="center"
                justifyContent="space-between"
                className={classes.detailsListItem}
              >
                <Typography variant="subtitle2" className={classes.detailsListText}>
                  {`${i18n.t('common.name')}:`}
                </Typography>
                <Typography variant="body2" className={classes.detailsListSecondText}>
                  {version.version}
                </Typography>
              </Box>
              {version.expiration_time && (
                <Box
                  pb={1}
                  pt={1}
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                  justifyContent="space-between"
                  className={classes.detailsListItem}
                >
                  <Typography variant="subtitle2" className={classes.detailsListText}>
                    {`${i18n.t('documents.expiration_date')}:`}
                  </Typography>
                  <Typography variant="body2" className={classes.detailsListSecondText}>
                    {moment(version.expiration_time).format(localeFormat)}
                  </Typography>
                </Box>
              )}
              {version.description && (
                <Box
                  pb={1}
                  pt={1}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  textAlign="left"
                  className={classes.detailsListItem}
                >
                  <Typography
                    variant="subtitle2"
                    style={{
                      width: '100%',
                    }}
                    className={classes.detailsListText}
                  >
                    {`${i18n.t('common.description')}:`}
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{
                      marginTop: 5,
                      width: '100%',
                    }}
                    className={classes.detailsListText}
                  >
                    {version.description}
                  </Typography>
                </Box>
              )}
              <Box mt={2} pb={1} pt={1} display="flex" flexDirection="column">
                <Typography variant="subtitle2" className={classes.detailsListText}>
                  {`${i18n.t('documents.file')}:`}
                </Typography>
                <Box display="flex">
                  <VersionFileInput
                    file={file}
                    fetchedUploadFile={fetchedUploadFile}
                    name="file"
                    onChange={onChangeAttr}
                    isUploading={isUploading}
                    onRemoveFile={() => handleRemoveFile(version.id)}
                  />
                </Box>
              </Box>
              {tags.length ? (
                <Box
                  mt={2}
                  pb={1}
                  pt={1}
                  display="flex"
                  flexDirection="row"
                  alignItems="flex-start"
                >
                  <Typography variant="subtitle2" className={classes.tagsText}>
                    {`${i18n.t('documents.tags')}:`}
                  </Typography>
                  <Box className={classes.chipContainer} ml={0.5}>
                    {tags.map(({ label }) => (
                      <Chip
                        key={label}
                        label={label}
                        clickable
                        color="primary"
                        onClick={() => onTagClick(label)}
                      />
                    ))}
                  </Box>
                </Box>
              ) : null}
            </Box>
          </Box>
        </>
      )}
      {Object.keys(version)?.length === 0 && !fetchingVersion && (
        <Typography>
          <Box fontSize={13} component="span" className={classes.emptyPlaceholder}>
            {i18n.t(`documents.empty_versions`)}
          </Box>
        </Typography>
      )}
      {versionModal && (
        <VersionModal
          mode={'update'}
          open={versionModal}
          handleCreate={onEdit}
          handleClose={toggleVersionModal}
          asyncErrors={asyncErrors.updateVersion}
          {...version}
        />
      )}
    </Card>
  );
}

VersionDetailsWidget.propTypes = {
  /**
   * Tags to be applied, would contain the linked objects to this document or version
   */
  tags: PropTypes.array,
  /**
   * Version of a document
   */
  version: PropTypes.any,
  /**
   * Changes handler for widget's values (status, file)
   */
  handleChange: PropTypes.func,
  /**
   * Handler for view details button
   */
  onViewDetails: PropTypes.func,
  /**
   * Handler for tags chips click
   */
  onTagClick: PropTypes.func,
  /**
   * Boolean to show details button
   */
  showDetailsBtn: PropTypes.bool,
};

VersionDetailsWidget.defaultProps = {
  tags: [],
  version: {},
  handleCreate: undefined,
  onViewDetails: undefined,
  onTagClick: undefined,
  showDetailsBtn: true,
};

export default VersionDetailsWidget;
